import { track } from './track';
import { EVENTS, SLOW_QUERY_THRESHOLD_MS } from './events';

type LegacyDataProvider = (
  type: string,
  resourceName: string,
  params: any,
) => Promise<any>;

const isReportResource = (resource: string): boolean =>
  resource.includes('reports/') || resource.includes('/reports/') || resource.includes('admin/metrics/');

const eventForMutation = (type: string): string | null => {
  switch (type) {
    case 'CREATE':
      return EVENTS.RESOURCE_CREATED;
    case 'UPDATE':
    case 'UPDATE_MANY':
      return EVENTS.RESOURCE_UPDATED;
    case 'DELETE':
    case 'DELETE_MANY':
      return EVENTS.RESOURCE_DELETED;
    default:
      return null;
  }
};

const countRows = (response: unknown): number | undefined => {
  if (!response || typeof response !== 'object') return undefined;
  const data = (response as { data?: unknown }).data;
  if (Array.isArray(data)) return data.length;
  return undefined;
};

const errorStatus = (err: unknown): number | undefined => {
  if (err && typeof err === 'object') {
    const status = (err as { status?: number; statusCode?: number }).status
      ?? (err as { statusCode?: number }).statusCode;
    if (typeof status === 'number') return status;
  }
  return undefined;
};

/**
 * Wraps the legacy (type, resource, params) dataProvider so every request
 * emits analytics. Errors and slow queries are auto-tracked; mutations
 * fire resource_created/updated/deleted; report fetches fire
 * report_opened / report_empty.
 *
 * The wrapper is transparent: it always returns the original promise.
 */
export const withAnalytics = (provider: LegacyDataProvider): LegacyDataProvider => {
  return async (type, resourceName, params) => {
    const startedAt = Date.now();
    const isReport = isReportResource(resourceName);
    const mutationEvent = eventForMutation(type);

    if (isReport && type === 'GET_LIST') {
      track(EVENTS.REPORT_OPENED, {
        resource: resourceName,
        filter_keys: params?.filter ? Object.keys(params.filter).join(',') : '',
        page: params?.pagination?.page ?? 1,
        per_page: params?.pagination?.perPage,
      });
    }

    try {
      const response = await provider(type, resourceName, params);
      const durationMs = Date.now() - startedAt;

      if (durationMs > SLOW_QUERY_THRESHOLD_MS) {
        track(EVENTS.SLOW_QUERY, {
          resource: resourceName,
          type,
          duration_ms: durationMs,
        });
      }

      if (isReport && type === 'GET_LIST' && countRows(response) === 0) {
        track(EVENTS.REPORT_EMPTY, {
          resource: resourceName,
          filter_keys: params?.filter ? Object.keys(params.filter).join(',') : '',
        });
      }

      if (mutationEvent) {
        track(mutationEvent, {
          resource: resourceName,
          duration_ms: durationMs,
        });
      }

      return response;
    } catch (err) {
      const status = errorStatus(err);
      // 401/403 are emitted as auth_logout_forced from authProvider, skip noise.
      if (status !== 401 && status !== 403) {
        track(EVENTS.API_ERROR, {
          resource: resourceName,
          type,
          status: status ?? 0,
          duration_ms: Date.now() - startedAt,
        });
      }
      throw err;
    }
  };
};
